import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar, Button, Card, Drawer, Empty, Input, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { CrownOutlined, SearchOutlined, UserOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getAdminCropUsersApi, getAdminCropUserStatesApi,
} from '../../api/admin';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import UserCropsTable, { YEAR_OPTIONS } from './UserCropsTable';
import CropCommonIdSelect from './CropCommonIdSelect';

const { Title, Text } = Typography;

export default function AdminCropGrowers() {
  const navigate = useNavigate();

  const [stateOptions, setStateOptions] = useState([]);

  const [commonId, setCommonId] = useState(null);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [year, setYear]         = useState(new Date().getFullYear());
  const [state, setState]       = useState(null);
  const [search, setSearch]     = useState('');

  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta]       = useState({ total: 0, current_page: 1, per_page: 20 });

  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    getAdminCropUserStatesApi()
      .then((res) => setStateOptions(res.data?.data || []))
      .catch(() => setStateOptions([]));
  }, []);

  const fetchUsers = useCallback(async (page = 1) => {
    if (commonId == null) { setUsers([]); setMeta((m) => ({ ...m, total: 0, current_page: 1 })); return; }
    setLoading(true);
    try {
      const params = { common_id: commonId, page, per_page: meta.per_page };
      if (year)   params.year   = year;
      if (state)  params.state  = state;
      if (search) params.search = search;
      const res = await getAdminCropUsersApi(params);
      setUsers(res.data?.data || []);
      setMeta(res.data?.meta || meta);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [commonId, year, state, search, meta.per_page]);

  useEffect(() => { fetchUsers(1); }, [commonId, year, state, search]);

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar
            src={r.photo ? USER_IMAGE(r.photo) : null}
            icon={<UserOutlined />}
            size={32}
            style={{ background: '#e8f5e9', color: '#1a6b2a', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name || '-'}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.country_code} {r.mobile}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 13 }}>{r.state || '-'}</div>
          {r.city && <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.city}</div>}
        </div>
      ),
    },
    {
      title: 'Crops',
      key: 'crops',
      width: 150,
      render: (_, r) => (
        <Space size={4}>
          <Tag color="green">{r.crops_count} added</Tag>
          {Number(r.active_count) > 0 && <Tag color="blue">{r.active_count} active</Tag>}
        </Space>
      ),
    },
    { title: 'Last Sown', dataIndex: 'last_crop_date', width: 120, render: formatDate },
    {
      title: 'Plan',
      key: 'plan',
      width: 90,
      render: (_, r) => (Number(r.isPrime) === 1
        ? <Tag color="gold" icon={<CrownOutlined />}>Prime</Tag>
        : <Tag color="default">Free</Tag>),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>Crop Growers</Title>

      <Card style={{ borderRadius: 10, marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
        <Space wrap size={12}>
          <CropCommonIdSelect
            value={commonId}
            onChange={(id, crop) => { setCommonId(id); setSelectedCrop(crop); }}
          />
          <Select
            value={year}
            onChange={(v) => setYear(v ?? null)}
            options={YEAR_OPTIONS}
            placeholder="All years"
            allowClear
            style={{ width: 130 }}
          />
          <Select
            showSearch
            value={state}
            onChange={(v) => setState(v ?? null)}
            placeholder="All states"
            allowClear
            optionFilterProp="label"
            style={{ width: 200 }}
            options={stateOptions.map((s) => ({ value: s.state, label: s.state }))}
          />
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search name, mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
        </Space>
      </Card>

      <Card style={{ borderRadius: 10 }}>
        {commonId == null ? (
          <Empty description="Select a crop to see the users who added it" />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={users}
            loading={loading}
            size="small"
            scroll={{ x: 800 }}
            onRow={(r) => ({ onClick: () => setSelectedUser(r), style: { cursor: 'pointer' } })}
            pagination={{
              current: meta.current_page,
              pageSize: meta.per_page,
              total: meta.total,
              showSizeChanger: false,
              showTotal: (t) => `${t} users`,
              onChange: (page) => fetchUsers(page),
            }}
          />
        )}
      </Card>

      <Drawer
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        width={Math.min(1100, window.innerWidth)}
        destroyOnHidden
        title={selectedUser && (
          <Space>
            <Avatar
              src={selectedUser.photo ? USER_IMAGE(selectedUser.photo) : null}
              icon={<UserOutlined />}
              style={{ background: '#e8f5e9', color: '#1a6b2a' }}
            />
            <div>
              <div>{selectedUser.name}</div>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                {selectedUser.country_code} {selectedUser.mobile}
                {selectedUser.state ? ` · ${selectedUser.state}` : ''}
              </Text>
            </div>
          </Space>
        )}
        extra={selectedUser && (
          <Button
            icon={<ArrowRightOutlined />}
            onClick={() => navigate(`/admin/users/${selectedUser.id}`, { state: { user: selectedUser, tab: 'crops' } })}
          >
            Open profile
          </Button>
        )}
      >
        {selectedUser && (
          <>
            {selectedCrop && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                All crops of this user. Rows for {selectedCrop.name} are highlighted.
              </Text>
            )}
            <UserCropsTable userId={selectedUser.id} highlightCommonId={commonId} />
          </>
        )}
      </Drawer>
    </div>
  );
}
